import { 
  FaUserTie, FaHandshake, FaUser, FaHome, FaFileExport, FaMobileAlt, 
  FaTimes, FaSeedling, FaFlask, FaBoxes, FaShoppingCart, 
  FaChevronDown, FaChevronRight, FaMap, FaUpload, 
  FaChartBar, FaFlag, FaServer, FaClinicMedical, FaHandHoldingHeart
} from 'react-icons/fa';
import {
  BsClipboard2DataFill,
  BsClipboard2PlusFill,
  BsCloudDownloadFill,
} from 'react-icons/bs';
import { LuLogs } from "react-icons/lu";
import { MdOutlineNoteAlt, MdDashboard, MdCalculate, MdScience, MdSettingsInputComponent, MdOutlineReportProblem } from "react-icons/md";
import { RiFormula } from "react-icons/ri";
import { PiTestTubeFill } from "react-icons/pi";
import { IoHardwareChip } from "react-icons/io5";
import { AiFillProduct } from "react-icons/ai";
import { ClipboardList, Target, Users, LayoutDashboard, Gauge, MapPin, Wheat } from "lucide-react";
import '../styles/sidebar.css';
import { useEffect, useRef } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { hasPermission } from '../hooks/usePermission';
import { useSidebar } from '../contexts/SidebarContext';
import { getRole, isCsrPartner } from '../utils/role';

const GENERAL_ITEMS = [
  { label: 'Home', icon: <FaHome />, path: '/', permission: null },
  { label: 'Soil Health Dashboard', icon: <MdDashboard />, path: '/partner/dashboard', permission: 'sidebar:dashboard' },
  { label: 'Farmers', icon: <FaUser />, path: '/users', permission: 'sidebar:farmers' },
  { label: 'Bulk Onboard', icon: <FaUpload />, path: '/bulk-onboard', permission: 'sidebar:bulk_onboard' },
  { label: 'Farms', icon: <Wheat size={16}/>, path: '/farms', permission: 'sidebar:farms' },
  { label: 'Reports', icon: <BsClipboard2DataFill />, path: '/reports', permission: 'sidebar:reports' },
  { label: 'Bookings', icon: <MdOutlineNoteAlt />, path: '/bookings', permission: 'sidebar:bookings' },
  { label: 'Raise Issue', icon: <MdOutlineReportProblem />, path: '/raise-issue', permission: 'sidebar:raise_issue' },
  { label: 'Field officers', icon: <FaUserTie />, path: '/field-officers', permission: 'sidebar:field_officers' },
  { label: 'Partners', icon: <FaHandshake />, path: '/partners', permission: 'sidebar:partners' },
  { label: 'Clinics', icon: <FaClinicMedical />, path: '/clinics', permission: 'sidebar:clinics' },
  { label: 'Create report', icon: <BsClipboard2PlusFill />, path: '/create-report', permission: 'reports:create' },
  { label: 'Update report', icon: <BsClipboard2PlusFill />, path: '/update-report', permission: 'reports:update' },
  { label: 'Bulk Update report', icon: <BsClipboard2PlusFill />, path: '/bulk-update-report', permission: 'reports:bulk_update' },
  { label: 'Download reports', icon: <BsCloudDownloadFill />, path: '/download-report', permission: 'reports:download' },
  { label: 'Add Product', icon: <FaBoxes />, path: '/add-product', permission: 'sidebar:add_product' },
  { label: 'Product List', icon: <AiFillProduct />, path: '/products', permission: 'sidebar:product_list' },
  { label: 'Product Orders', icon: <FaShoppingCart />, path: '/product-orders', permission: 'sidebar:product_orders' },
  { label: 'Export', icon: <FaFileExport />, path: '/export', permission: 'sidebar:export' },
  { label: 'Recommendations', icon: <FaSeedling />, path: '/recommendations', permission: 'sidebar:recommendations' },
  { label: 'Parameter Formula', icon: <RiFormula />, path: '/parameter-formula', permission: 'sidebar:Parameter-Formula' },
  { label: 'Nutrient Calculator', icon: <MdCalculate />, path: '/nutrient-calculator', permission: 'sidebar:nutrient_calculator' },
  { label: 'Farm Map', icon: <MapPin size={16} />, path: '/support/farms-map', permission: 'sidebar:farms_map' },
  { label: 'Map Devices', icon: <FaMap />, path: '/support/map-devices', permission: 'sidebar:map_devices' },
  { label: 'Logs', icon: <LuLogs />, path: '/data-logs', permission: 'sidebar:logs' },
  { label: 'Anomaly Logs', icon: <LuLogs />, path: '/anomaly-logs', permission: 'sidebar:anomaly_logs' },
  { label: 'Issue Management', icon: <ClipboardList size={16} />, path: '/support/issues', permission: 'sidebar:issue_management' },
];

const IT_LOGS_GROUP_ITEMS = [
  { label: 'API Analytics', icon: <FaChartBar />, path: '/api-analytics/dashboard', permission: 'sidebar:it_logs_group' },
  { label: 'Feature Flags', icon: <FaFlag />, path: '/feature-flags', permission: 'sidebar:it_logs_group' },
];

const DEVICE_GROUP_ITEMS = [
  { label: 'Hardware Version', icon: <IoHardwareChip />, path: '/device/hardware-version', permission: 'sidebar:device_hw_version' },
  { label: 'Software Version', icon: <MdSettingsInputComponent />, path: '/device/software-version', permission: 'sidebar:device_sw_version' },
];

const LAB_GROUP_ITEMS = [
  { label: 'Lab Dashboard', icon: <LayoutDashboard size={16} />, path: '/lab/dashboard', permission: 'sidebar:lab_dashboard' },
  { label: 'Reagents', icon: <FaFlask />, path: '/lab/reagents', permission: 'sidebar:reagents' },
  { label: 'Reagents Stock', icon: <FaBoxes />, path: '/lab/stock', permission: 'sidebar:stock' },
  { label: 'Chemical Calculator', icon: <MdCalculate />, path: '/lab/calculator', permission: 'sidebar:calculator' },
  { label: 'Raw Chemicals', icon: <MdScience />, path: '/lab/raw-chemicals', permission: 'sidebar:raw_chemicals' },
  { label: 'Soil Orders', icon: <FaShoppingCart />, path: '/lab/orders', permission: 'sidebar:orders' },
  { label: 'Soil Test Capacity', icon: <PiTestTubeFill />, path: '/lab/test-capacity', permission: 'sidebar:test_capacity' },
  { label: 'Crew', icon: <Users size={16} />, path: '/lab/crew', permission: 'sidebar:lab_crew' },
  { label: 'Work Logs', icon: <ClipboardList size={16} />, path: '/lab/work-logs', permission: 'sidebar:lab_work_logs' },
  { label: 'Targets', icon: <Target size={16} />, path: '/lab/targets', permission: 'sidebar:lab_targets' },
  { label: 'EC Components', icon: <MdSettingsInputComponent />, path: '/lab/electronic-components', permission: 'sidebar:electronic-components' },
  { label: 'Hardware', icon: <IoHardwareChip />, path: '/lab/hardware', permission: 'sidebar:hardware' },
  { label: 'Hardware Order', icon: <FaShoppingCart />, path: '/lab/hardware-order', permission: 'sidebar:hardware-order' },
  { label: 'Hardware Capacity', icon: <Gauge size={16} />, path: '/lab/hardware-capacity', permission: 'sidebar:hardware-capacity' },
];

const CSR_GROUP_ITEMS = [
  { label: 'Overview', icon: <LayoutDashboard size={16} />, path: '/csr/overview' },
];

const Sidebar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const sidebarRef = useRef(null);
  const { isOpen, close } = useSidebar();

  const isActive = (path) => pathname === path || pathname.startsWith(path + '/');

  const isLabSectionActive = pathname.startsWith('/lab/');
  const isDeviceSectionActive = pathname.startsWith('/device');
  const isItLogsSectionActive = pathname.startsWith('/api-analytics') || pathname.startsWith('/feature-flags')
  const isCsrSectionActive = pathname.startsWith('/csr/');
  const csrSupportAdminId = getRole() === 'SUPPORT'
    ? matchPath('/csr/:tab/:adminId', pathname)?.params?.adminId
    : null;
  const canViewCsrSection = isCsrPartner() || Boolean(csrSupportAdminId);
  const csrPath = (path) => (csrSupportAdminId ? `${path}/${csrSupportAdminId}` : path);
  const hasAnyVisibleLabItem = LAB_GROUP_ITEMS.some((item) => !item.permission || hasPermission(item.permission));
  const canViewDeviceRoot = hasPermission('sidebar:device');
  const hasAnyVisibleDeviceItem = DEVICE_GROUP_ITEMS.some((item) => !item.permission || hasPermission(item.permission));
  const canViewItLogsRoot = hasPermission('sidebar:it_logs_group');
  const hasAnyVisibleItLogsItem = IT_LOGS_GROUP_ITEMS.some((item) => !item.permission || hasPermission(item.permission));

  const handleNavigate = (path) => {
    navigate(path);
    if (window.innerWidth <= 768 && close) {
      close();
    }
  };

  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    const saved = window.sessionStorage.getItem('sidebar-scroll-top');
    if (saved !== null) {
      el.scrollTop = Number(saved);
    }
  }, [pathname]);

  const handleSidebarScroll = (e) => {
    window.sessionStorage.setItem('sidebar-scroll-top', String(e.currentTarget.scrollTop));
  };

  return (
    <div
      ref={sidebarRef}
      onScroll={handleSidebarScroll}
      className={`border-end sidebar me-2 d-flex flex-column py-4 px-1 gap-1 ${isOpen ? 'show' : ''}`}
    >
      {/* Close button for mobile */}
      <div className="d-flex justify-content-end d-md-none mb-2 px-2">
        <button
          onClick={close}
          className="btn btn-sm"
          style={{ border: 'none', background: 'transparent', fontSize: '20px' }}
          aria-label="Close sidebar"
        >
          <FaTimes />
        </button>
      </div>

      {/* General Items */}
      {GENERAL_ITEMS.map((item) => {
        if (item.permission && !hasPermission(item.permission)) return null;

        return (
          <div
            key={item.path}
            className={`d-flex align-items-center p-2 gap-2 rounded-3 sidebar-item ${isActive(item.path) ? 'bg-dark text-light' : ''}`}
            onClick={() => handleNavigate(item.path)}
          >
            <span className="sidebar-item-icon">{item.icon}</span>
            <span className="sidebar-item-label">{item.label}</span>
          </div>
        );
      })}

      {canViewCsrSection ? (
        <div className="d-flex flex-column">
          <div
            className={`d-flex align-items-center justify-content-between p-2 gap-2 rounded-3 sidebar-item ${isCsrSectionActive ? 'bg-dark text-light' : ''}`}
            onClick={() => handleNavigate(csrPath(CSR_GROUP_ITEMS[0].path))}
          >
            <div className="d-flex align-items-center gap-2">
              <span className="sidebar-item-icon"><FaHandHoldingHeart /></span>
              <span className="sidebar-item-label">CSR Impact</span>
            </div>
            <span className="sidebar-item-icon">
              {isCsrSectionActive ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
            </span>
          </div>

          {isCsrSectionActive ? (
            <div className="d-flex flex-column mt-1 ms-3 ps-2" style={{ borderLeft: '1px solid #d5d5d5' }}>
              {CSR_GROUP_ITEMS.map((item) => (
                <div
                  key={item.path}
                  className={`d-flex align-items-center p-2 gap-2 rounded-3 sidebar-item ${isActive(item.path) ? 'bg-dark text-light' : ''}`}
                  onClick={() => handleNavigate(csrPath(item.path))}
                >
                  <span className="sidebar-item-icon">{item.icon}</span>
                  <span className="sidebar-item-label">{item.label}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* IT Logs Group */}
      {canViewItLogsRoot ? (
        <div className="d-flex flex-column">
          <div
            className={`d-flex align-items-center justify-content-between p-2 gap-2 rounded-3 sidebar-item ${isItLogsSectionActive ? 'bg-dark text-light' : ''}`}
            onClick={() => handleNavigate('/api-analytics/dashboard')}
          >
            <div className="d-flex align-items-center gap-2">
              <span className="sidebar-item-icon"><FaServer /></span>
              <span className="sidebar-item-label">IT Logs</span>
            </div>
            <span className="sidebar-item-icon">
              {isItLogsSectionActive ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
            </span>
          </div>

          {isItLogsSectionActive && hasAnyVisibleItLogsItem ? (
            <div className="d-flex flex-column mt-1 ms-3 ps-2" style={{ borderLeft: '1px solid #d5d5d5' }}>
              {IT_LOGS_GROUP_ITEMS.map((item) => {
                if (item.permission && !hasPermission(item.permission)) return null;

                return (
                  <div
                    key={item.path}
                    className={`d-flex align-items-center p-2 gap-2 rounded-3 sidebar-item ${isActive(item.path) ? 'bg-dark text-light' : ''}`}
                    onClick={() => handleNavigate(item.path)}
                  >
                    <span className="sidebar-item-icon">{item.icon}</span>
                    <span className="sidebar-item-label">{item.label}</span>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Device Group */}
      {canViewDeviceRoot ? (
        <div className="d-flex flex-column">
          <div
            className={`d-flex align-items-center justify-content-between p-2 gap-2 rounded-3 sidebar-item ${isDeviceSectionActive ? 'bg-dark text-light' : ''}`}
            onClick={() => handleNavigate('/device')}
          >
            <div className="d-flex align-items-center gap-2">
              <span className="sidebar-item-icon"><FaMobileAlt /></span>
              <span className="sidebar-item-label">Device</span>
            </div>
            <span className="sidebar-item-icon">
              {isDeviceSectionActive ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
            </span>
          </div>

          {isDeviceSectionActive && hasAnyVisibleDeviceItem ? (
            <div className="d-flex flex-column mt-1 ms-3 ps-2" style={{ borderLeft: '1px solid #d5d5d5' }}>
              {DEVICE_GROUP_ITEMS.map((item) => {
                if (item.permission && !hasPermission(item.permission)) return null;

                return (
                  <div
                    key={item.path}
                    className={`d-flex align-items-center p-2 gap-2 rounded-3 sidebar-item ${isActive(item.path) ? 'bg-dark text-light' : ''}`}
                    onClick={() => handleNavigate(item.path)}
                  >
                    <span className="sidebar-item-icon">{item.icon}</span>
                    <span className="sidebar-item-label">{item.label}</span>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Lab Group */}
      {hasAnyVisibleLabItem ? (
        <div className="d-flex flex-column">
          <div
            className={`d-flex align-items-center justify-content-between p-2 gap-2 rounded-3 sidebar-item ${isLabSectionActive ? 'bg-dark text-light' : ''}`}
            onClick={() => handleNavigate('/lab/dashboard')}
          >
            <div className="d-flex align-items-center gap-2">
              <span className="sidebar-item-icon"><LayoutDashboard size={16} /></span>
              <span className="sidebar-item-label">Lab Dashboard</span>
            </div>
            <span className="sidebar-item-icon">
              {isLabSectionActive ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
            </span>
          </div>

          {isLabSectionActive ? (
            <div className="d-flex flex-column mt-1 ms-3 ps-2" style={{ borderLeft: '1px solid #d5d5d5' }}>
              {LAB_GROUP_ITEMS.map((item) => {
                if (item.permission && !hasPermission(item.permission)) return null;

                return (
                  <div
                    key={item.path}
                    className={`d-flex align-items-center p-2 gap-2 rounded-3 sidebar-item ${isActive(item.path) ? 'bg-dark text-light' : ''}`}
                    onClick={() => handleNavigate(item.path)}
                  >
                    <span className="sidebar-item-icon">{item.icon}</span>
                    <span className="sidebar-item-label">{item.label}</span>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default Sidebar;